<template>
  <div>
    <!-- Payment Status Summary Cards -->
    <CRow class="mb-4">
      <CCol md="4">
        <CCard class="text-center border-0 shadow-sm">
          <CCardBody class="p-3">
            <div class="text-muted small mb-1">Total</div>
            <div class="h5 mb-0 font-weight-bold">RM {{ formatCurrency(billTotal) }}</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="4">
        <CCard class="text-center border-0 shadow-sm">
          <CCardBody class="p-3">
            <div class="small mb-1 text-muted">Paid</div>
            <div class="h5 mb-0 font-weight-bold" :class="totalPaid > 0 ? 'text-success' : ''">RM {{ formatCurrency(totalPaid) }}</div>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol md="4">
        <CCard class="text-center border-0 shadow-sm">
          <CCardBody class="p-3">
            <div class="small mb-1 text-muted">Amount due</div>
            <div class="h5 mb-0 font-weight-bold">RM {{ formatCurrency(amountDue) }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Payment Status Badge -->
    <div class="mb-3">
      <CBadge :color="paymentStatusBadgeColor" class="p-2">
        {{ paymentStatusText }}
      </CBadge>
    </div>

    <!-- Desktop Table View -->
    <div class="table-responsive d-none d-md-block">
      <table class="table table-hover border">
        <thead>
          <tr>
            <th style="width: 50px">#</th>
            <th style="width: 150px">Payment date</th>
            <th style="width: 150px" class="text-right">Amount (RM)</th>
            <th style="width: 150px">Method</th>
            <th style="width: 150px">Reference</th>
            <th style="width: 200px">Proof</th>
            <th>Notes</th>
            <th style="width: 100px">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(payment, index) in payments" :key="payment.id || index">
            <td>{{ index + 1 }}</td>
            <td>
              <CInput
                type="date"
                :value="formatPaymentDate(payment.paymentDate)"
                @input="setPaymentDate(payment, $event)"
                :disabled="isReadOnly"
              />
            </td>
            <td>
              <CInput
                type="number"
                inputmode="decimal"
                v-model="payment.amount"
                step="0.01"
                min="0"
                aria-label="Amount"
                :disabled="isReadOnly"
                @blur="onPaymentChange(payment)"
              />
            </td>
            <td>
              <v-select
                v-model="payment.paymentMethod"
                :options="paymentMethodOptions"
                :clearable="false"
                :disabled="isReadOnly"
                @input="onPaymentChange(payment)"
              />
            </td>
            <td>
              <CInput
                v-model="payment.paymentReference"
                placeholder="Ref no."
                :disabled="isReadOnly"
                @blur="onPaymentChange(payment)"
              />
            </td>
            <td class="py-2">
              <!-- No proof uploaded -->
              <div v-if="!payment.documentPaymentProof">
                <input
                  type="file"
                  :ref="'proofInput_' + index"
                  style="display: none"
                  accept="image/*,.pdf"
                  @change="onProofFileSelected(payment, index, $event)"
                  :disabled="isReadOnly || uploadingProofForIndex === index"
                />
                <CButton
                  color="light"
                  size="sm"
                  @click="triggerProofUpload(payment, index)"
                  :disabled="isReadOnly || uploadingProofForIndex === index"
                  class="w-100"
                >
                  <CIcon name="cil-paperclip" size="sm" />
                  {{ uploadingProofForIndex === index ? 'Uploading…' : 'Attach' }}
                </CButton>
              </div>

              <!-- Proof uploaded - Show thumbnail -->
              <div v-else class="d-flex align-items-start">
                <!-- Thumbnail -->
                <div
                  class="proof-thumbnail mr-2"
                  :class="isProofPdf(payment.documentPaymentProof) ? 'pdf-thumbnail' : 'image-thumbnail'"
                  @click="viewProofPreview(payment)"
                  style="cursor: pointer"
                >
                  <img
                    v-if="!isProofPdf(payment.documentPaymentProof)"
                    :src="getProofUrl(payment.documentPaymentProof)"
                    class="img-thumbnail"
                    style="width: 40px; height: 40px; object-fit: cover;"
                  />
                  <div v-else class="pdf-icon-container">
                    <CIcon name="cil-file-pdf" size="xl" class="text-danger" />
                  </div>
                </div>

                <!-- File info and actions -->
                <div class="flex-grow-1" style="min-width: 0;">
                  <div
                    class="small text-truncate"
                    :title="payment.documentPaymentProof.fileName"
                    style="max-width: 100px;"
                  >
                    {{ payment.documentPaymentProof.fileName }}
                  </div>
                  <div class="mt-1">
                    <CButton
                      color="info"
                      size="sm"
                      @click="viewProofPreview(payment)"
                      class="p-1"
                      v-c-tooltip="'Preview'"
                      aria-label="Preview payment proof"
                    >
                      <CIcon name="cil-search" size="sm" />
                    </CButton>
                    <CButton
                      color="primary"
                      size="sm"
                      @click="downloadProof(payment)"
                      class="p-1 ml-1"
                      v-c-tooltip="'Open in new tab'"
                      aria-label="Open payment proof in a new tab"
                    >
                      <CIcon name="cil-external-link" size="sm" />
                    </CButton>
                    <CButton
                      color="danger"
                      size="sm"
                      @click="confirmRemoveProof(payment, index)"
                      class="p-1 ml-1"
                      :disabled="isReadOnly"
                      v-c-tooltip="'Remove'"
                      aria-label="Remove payment proof"
                    >
                      <CIcon name="cil-trash" size="sm" />
                    </CButton>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <CTextarea
                v-model="payment.paymentNotes"
                rows="1"
                :disabled="isReadOnly"
                @blur="onPaymentChange(payment)"
              />
            </td>
            <td>
              <CButton
                v-if="enableReceipt"
                color="success"
                size="sm"
                class="mr-1"
                @click="$emit('generate-receipt', payment)"
                :disabled="!payment.isSaved"
                :title="payment.isSaved ? 'Generate official receipt (PDF)' : 'Save the invoice first to generate a receipt'"
                aria-label="Generate official receipt"
              >
                <CIcon name="cil-file" />
              </CButton>
              <CButton
                color="danger"
                size="sm"
                @click="removePayment(index)"
                :disabled="isReadOnly"
                title="Remove"
                aria-label="Remove payment"
              >
                <CIcon name="cil-trash" />
              </CButton>
            </td>
          </tr>
          <tr v-if="payments.length === 0">
            <td colspan="8" class="text-center py-4">
              <div class="text-muted">
                <CIcon name="cil-dollar" size="xl" />
                <p class="mt-2">No payments recorded yet</p>
                <CButton
                  v-if="!isReadOnly"
                  color="primary"
                  size="sm"
                  @click="addPayment"
                  :disabled="isFullPaid"
                >
                  Add payment
                </CButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div class="d-md-none">
      <div
        v-for="(payment, index) in payments"
        :key="payment.id || index"
        class="mb-3 border rounded p-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">Payment {{ index + 1 }}</h6>
          <div>
            <CButton
              v-if="enableReceipt"
              color="success"
              size="sm"
              class="mr-1"
              @click="$emit('generate-receipt', payment)"
              :disabled="!payment.isSaved"
              :title="payment.isSaved ? 'Generate official receipt (PDF)' : 'Save the invoice first to generate a receipt'"
            >
              <CIcon name="cil-file" /> Receipt
            </CButton>
            <CButton
              color="danger"
              size="sm"
              @click="removePayment(index)"
              :disabled="isReadOnly"
              aria-label="Remove payment"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </div>
        </div>

        <div class="form-group">
          <label>Payment date</label>
          <CInput
            type="date"
            :value="formatPaymentDate(payment.paymentDate)"
            @input="setPaymentDate(payment, $event)"
            :disabled="isReadOnly"
          />
        </div>

        <div class="form-group">
          <label>Amount (RM)</label>
          <CInput
            type="number"
            inputmode="decimal"
            v-model="payment.amount"
            step="0.01"
            min="0"
            :disabled="isReadOnly"
            @blur="onPaymentChange(payment)"
          />
        </div>

        <div class="form-group">
          <label>Payment method</label>
          <v-select
            v-model="payment.paymentMethod"
            :options="paymentMethodOptions"
            :clearable="false"
            :disabled="isReadOnly"
            @input="onPaymentChange(payment)"
          />
        </div>

        <div class="form-group">
          <label>Reference</label>
          <CInput
            v-model="payment.paymentReference"
            placeholder="e.g. cheque no. or transaction ID"
            :disabled="isReadOnly"
            @blur="onPaymentChange(payment)"
          />
        </div>

        <div class="form-group">
          <label>Notes</label>
          <CTextarea
            v-model="payment.paymentNotes"
            rows="2"
            :disabled="isReadOnly"
            @blur="onPaymentChange(payment)"
          />
        </div>

        <div class="form-group">
          <label>Payment proof</label>

          <!-- No proof -->
          <div v-if="!payment.documentPaymentProof">
            <input
              type="file"
              :ref="'proofInputMobile_' + index"
              style="display: none"
              accept="image/*,.pdf"
              @change="onProofFileSelected(payment, index, $event)"
              :disabled="isReadOnly || uploadingProofForIndex === index"
            />
            <CButton
              color="light"
              block
              @click="triggerProofUpload(payment, index)"
              :disabled="isReadOnly || uploadingProofForIndex === index"
            >
              <CIcon name="cil-paperclip" />
              {{ uploadingProofForIndex === index ? 'Uploading…' : 'Attach proof' }}
            </CButton>
          </div>

          <!-- Proof uploaded -->
          <div v-else class="border rounded p-2">
            <div class="d-flex align-items-center mb-2">
              <!-- Thumbnail -->
              <div
                class="proof-thumbnail mr-2"
                :class="isProofPdf(payment.documentPaymentProof) ? 'pdf-thumbnail' : 'image-thumbnail'"
              >
                <img
                  v-if="!isProofPdf(payment.documentPaymentProof)"
                  :src="getProofUrl(payment.documentPaymentProof)"
                  style="width: 40px; height: 40px; object-fit: cover;"
                />
                <div v-else class="pdf-icon-container">
                  <CIcon name="cil-file-pdf" size="lg" class="text-danger" />
                </div>
              </div>

              <!-- Filename -->
              <div class="small text-truncate flex-grow-1">
                {{ payment.documentPaymentProof.fileName }}
              </div>
            </div>

            <!-- Action buttons -->
            <div class="d-flex" style="gap: 0.5rem;">
              <CButton
                color="info"
                size="sm"
                @click="viewProofPreview(payment)"
                class="flex-grow-1"
              >
                <CIcon name="cil-search" size="sm" /> View
              </CButton>
              <CButton
                color="primary"
                size="sm"
                @click="downloadProof(payment)"
                class="flex-grow-1"
              >
                <CIcon name="cil-external-link" size="sm" /> Open
              </CButton>
              <CButton
                color="danger"
                size="sm"
                @click="confirmRemoveProof(payment, index)"
                :disabled="isReadOnly"
                class="flex-grow-1"
              >
                <CIcon name="cil-trash" size="sm" /> Remove
              </CButton>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="payments.length === 0"
        class="text-center py-4 border rounded"
      >
        <div class="text-muted">
          <CIcon name="cil-dollar" size="xl" />
          <p class="mt-2">No payments recorded yet</p>
          <CButton
            v-if="!isReadOnly"
            color="primary"
            size="sm"
            @click="addPayment"
            :disabled="isFullPaid"
          >
            Add payment
          </CButton>
        </div>
      </div>
    </div>

    <!-- Add Payment Button (Desktop) -->
    <div class="mt-3 d-none d-md-block" v-if="!isReadOnly && payments.length > 0">
      <CButton
        color="primary"
        @click="addPayment"
        :disabled="isFullPaid"
      >
        <CIcon name="cil-plus" /> Add payment
      </CButton>
      <span v-if="isFullPaid" class="text-success ml-3">
        <CIcon name="cil-check-circle" /> Fully paid
      </span>
    </div>

    <!-- Proof Preview Modal -->
    <CModal
      title="Payment proof"
      :show.sync="showProofPreviewModal"
      size="lg"
      :close-on-backdrop="false"
    >
      <div v-if="previewProof">
        <!-- PDF Preview -->
        <iframe
          v-if="isProofPdf(previewProof)"
          :src="getProofUrl(previewProof)"
          width="100%"
          height="600px"
          style="border: none;"
        ></iframe>

        <!-- Image Preview -->
        <img
          v-else
          :src="getProofUrl(previewProof)"
          class="img-fluid"
          style="max-width: 100%;"
        />
      </div>

      <template #footer>
        <CButton color="light" @click="showProofPreviewModal = false">
          Close
        </CButton>
        <CButton color="primary" @click="downloadProof({ documentPaymentProof: previewProof })">
          <CIcon name="cil-external-link" /> Open in new tab
        </CButton>
      </template>
    </CModal>

    <!-- Remove Proof Confirmation Modal -->
    <CModal
      title="Remove this payment proof?"
      :show.sync="showRemoveProofModal"
    >
      The attached file will be removed from this payment. The payment itself stays recorded.
      <template #footer>
        <CButton color="light" @click="showRemoveProofModal = false; proofToRemove = null;">
          Keep it
        </CButton>
        <CButton color="danger" @click="removeProof(); showRemoveProofModal = false;">
          Remove proof
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, parseDateInput, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import FileUploadApi from "../../lib/fileUploadApi";

export default {
  name: "BillPaymentsComponent",
  components: {
    vSelect,
  },
  props: {
    payments: {
      type: Array,
      default: () => [],
    },
    billTotal: {
      type: Number,
      default: 0,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    // When true (invoice context), show a per-payment "Official Receipt" action.
    enableReceipt: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    payments: {
      handler() {
        // Watch for payment prop changes
      },
      immediate: true,
      deep: true
    }
  },
  data() {
    return {
      paymentMethodOptions: [
        "Bank Transfer",
        "Cash",
        "Cheque",
        "Credit Card",
        "Debit Card",
        "Online Banking",
        "Others",
      ],
      uploadingProofForIndex: null,
      previewProof: null,
      showProofPreviewModal: false,
      showRemoveProofModal: false,
      proofToRemove: null,
      fileUploadApi: new FileUploadApi(),
    };
  },
  computed: {
    totalPaid() {
      return this.payments.reduce((sum, p) => {
        return sum + (parseFloat(p.amount) || 0);
      }, 0);
    },

    amountDue() {
      return Math.max(0, this.billTotal - this.totalPaid);
    },

    isFullPaid() {
      return Math.abs(this.amountDue) < 0.01;
    },

    isPartialPaid() {
      return this.totalPaid > 0 && !this.isFullPaid;
    },

    paymentStatusKey() {
      if (this.isFullPaid) return "Paid";
      if (this.isPartialPaid) return "Partial";
      return "Unpaid";
    },

    paymentStatusText() {
      return badgeLabel("documentPayment", this.paymentStatusKey);
    },

    paymentStatusBadgeColor() {
      return badgeColor("documentPayment", this.paymentStatusKey);
    },
  },
  methods: {
    addPayment() {
      const newPayment = {
        paymentDate: mytToday(),
        amount: this.amountDue > 0 ? this.amountDue : 0,
        paymentMethod: "Bank Transfer",
        paymentReference: "",
        paymentNotes: "",
      };
      this.$emit("add-payment", newPayment);
    },

    removePayment(index) {
      this.$emit("remove-payment", index);
    },

    onPaymentChange(payment) {
      this.$emit("payment-change", payment);
    },

    formatPaymentDate(paymentDate) {
      // Format DateTime to YYYY-MM-DD for HTML5 date input
      return asDateString(paymentDate);
    },

    setPaymentDate(payment, value) {
      // Keep the wall-clock date as "yyyy-MM-dd" (a Date object would shift by 8 hours)
      payment.paymentDate = parseDateInput(value) || null;
      this.onPaymentChange(payment);
    },

    // Number part only; the template adds "RM ".
    formatCurrency(amount) {
      return formatMyr(amount);
    },

    // Proof upload methods
    triggerProofUpload(payment, index) {
      const input = this.$refs['proofInput_' + index];
      if (input && input[0]) {
        input[0].click();
      }
    },

    onProofFileSelected(payment, index, event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$emit('show-toast', 'Error', 'This file is larger than 10 MB. Choose a smaller file.', 'danger');
        event.target.value = ''; // Reset input
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$emit('show-toast', 'Error', 'Choose a JPG, PNG or PDF file.', 'danger');
        event.target.value = '';
        return;
      }

      this.uploadProof(payment, index, file);
    },

    async uploadProof(payment, index, file) {
      this.uploadingProofForIndex = index;
      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await this.fileUploadApi.uploadDocument(formData);
        const uploadedFile = response.result[0];

        // Set document info
        payment.documentPaymentProofId = uploadedFile.id;
        payment.documentPaymentProof = {
          id: uploadedFile.id,
          contentType: uploadedFile.contentType,
          fileName: uploadedFile.fileName
        };

        this.$emit('show-toast', 'Success', 'Payment proof uploaded', 'success');
        this.$emit('payment-proof-uploaded', index, uploadedFile);

        // Reset file input
        const input = this.$refs['proofInput_' + index];
        if (input && input[0]) input[0].value = '';
      } catch (error) {
        console.error('Failed to upload proof:', error);
        this.$emit('show-toast', 'Error', "Couldn't upload the payment proof. Please try again.", 'danger');
      } finally {
        this.uploadingProofForIndex = null;
      }
    },

    viewProofPreview(payment) {
      if (payment.documentPaymentProof) {
        this.previewProof = payment.documentPaymentProof;
        this.showProofPreviewModal = true;
      }
    },

    downloadProof(payment) {
      if (payment.documentPaymentProof) {
        const url = this.getProofUrl(payment.documentPaymentProof);
        window.open(url, '_blank');
      }
    },

    confirmRemoveProof(payment, index) {
      this.proofToRemove = { payment, index };
      this.showRemoveProofModal = true;
    },

    removeProof() {
      if (this.proofToRemove) {
        const { payment, index } = this.proofToRemove;
        payment.documentPaymentProofId = null;
        payment.documentPaymentProof = null;

        this.$emit('show-toast', 'Success', 'Payment proof removed', 'success');
        this.$emit('payment-proof-removed', index);

        this.proofToRemove = null;
      }
    },

    getProofUrl(document) {
      return apiUrl + 'documents/file/' + document.id;
    },

    isProofPdf(document) {
      return document?.contentType === 'application/pdf';
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.875rem;
}

.proof-thumbnail {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
}

.pdf-thumbnail {
  border: 1px solid var(--light, currentColor);
}

.pdf-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.image-thumbnail img {
  border-radius: 4px;
}

@media (max-width: 767.98px) {
  .table-responsive {
    display: none !important;
  }
}
</style>
